import { useCallback, useMemo } from "react";

import type { DropdownOption } from "../../../DropDownV2";
import type { MultiSelectOption } from "../../../MultiSelect";
import type {
  ClientsPageAnalytics,
  ReturnedTranslatedContentClientsPageHook,
} from "../../types";
import type { ClientAccountTableFilterSubProps } from "./types";

import { useIsMobile } from "../../../hooks";
import { useDebounceFn } from "../../../utils/debounce";
import { PII_FIELDS } from "../consts";
import { useClientAccountReportContext } from "../providers/AccountReportProvider";
import { DesktopFilters } from "./DesktopFilters";
import { MobileFilters } from "./MobileFilters";
import { MobileFiltersV2 } from "./MobileFiltersV2";

export type ClientAccountReportFiltersProps = {
  analytics?: ClientsPageAnalytics;
  translations: ReturnedTranslatedContentClientsPageHook;
  isMobileBottomSheetEnable?: boolean;
  /** Gated by the part-3795-materialized-view-hoid flag. */
  showRebateProgramFilter?: boolean;
};

export const ClientAccountReportFilters = ({
  analytics,
  translations,
  isMobileBottomSheetEnable = false,
  showRebateProgramFilter,
}: ClientAccountReportFiltersProps) => {
  const isMobile = useIsMobile();

  // No default rebate account/program selection -- until the user picks one
  // via the Filter modal, the report shows the full IB data.
  const {
    state: { selected },
    resources: {
      table: { data: tableData },
    },
    actions,
    runResource,
  } = useClientAccountReportContext();

  const canViewPII = !PII_FIELDS.every((field) =>
    tableData?.redactedFields?.includes(field),
  );

  const [handleSearchInput] = useDebounceFn((value: string) => {
    actions.setSearchQuery(value);
    analytics?.trackTableSearchFieldChangeEvent("Account report", value);
  }, 1000);

  const handleRebateAccountsDropdown = useCallback(
    (options: DropdownOption[]) => {
      const newRebateAccountIDs = options.map((o) => o.value);
      if (
        JSON.stringify(newRebateAccountIDs) !==
        JSON.stringify(selected.rebateAccountIDs)
      ) {
        analytics?.trackTableRebateSelectEventV2(
          "Account report",
          newRebateAccountIDs,
        );
      }

      actions.setSelected("rebateAccountIDs", newRebateAccountIDs);
    },
    [actions, analytics, selected.rebateAccountIDs],
  );

  const handleRebateProgramsMultiSelect = useCallback(
    (options: MultiSelectOption[]) => {
      actions.setSelected(
        "rebateProgramIDs",
        options.map((o) => o.value),
      );
    },
    [actions],
  );

  const filterConfig = useMemo<ClientAccountTableFilterSubProps>(
    () => ({
      handleExportButton: () => runResource("csv"),
      handleRebateAccountsDropdown,
      handleRebateProgramsMultiSelect,
      handleSearchInput,
      canViewPII,
      redactedFields: tableData?.redactedFields,
      translations,
      showRebateProgramFilter,
    }),
    [
      handleRebateAccountsDropdown,
      handleRebateProgramsMultiSelect,
      handleSearchInput,
      canViewPII,
      tableData?.redactedFields,
      translations,
      runResource,
      showRebateProgramFilter,
    ],
  );

  if (isMobile && isMobileBottomSheetEnable) {
    return (
      <MobileFiltersV2
        handleSearchInput={handleSearchInput}
        canViewPII={canViewPII}
        redactedFields={tableData?.redactedFields}
        showRebateProgramFilter={showRebateProgramFilter}
      />
    );
  }

  return isMobile ? (
    <MobileFilters {...filterConfig} />
  ) : (
    <DesktopFilters {...filterConfig} />
  );
};
